/**
 * Static game content. This is where FinOps concepts become mechanics:
 *
 * - Every building and unit has a BURN RATE ($/sec) — your cloud bill never
 *   sleeps. Revenue minus burn is the economy (unit economics).
 * - GPU clusters generate TOKENS; agents spend tokens to do anything.
 *   Cost-optimization techs (prompt caching, model routing, batching) reduce
 *   token spend or raise throughput — usage optimization.
 * - Commitment-discount techs cut burn in exchange for upfront committed
 *   spend — rate optimization.
 * - Phases mirror the FinOps Framework maturity model: Crawl -> Walk -> Run.
 *
 * All rates are expressed PER SECOND and converted to per-tick in the sim.
 */

import type { BuildingType, FactionId, Phase, TechId, UnitType } from './types';

// ---------------------------------------------------------------------------
// Global economy tuning
// ---------------------------------------------------------------------------

export const ECONOMY = {
  startingBudget: 2500,
  startingTokens: 100,
  baseTokenCap: 200,
  /** $ earned per serve action at a market (before modifiers) */
  revenuePerServe: 14,
  /** tokens consumed per serve action (before modifiers) */
  tokensPerServe: 6,
  /** seconds between serve actions for an inference agent */
  servePeriodSec: 1.5,
  /** data harvested per gather trip */
  gatherCarry: 10,
  /** seconds spent mining at a node per trip */
  gatherTimeSec: 3,
  /** budget may go negative for this long before elimination */
  insolvencyGraceSec: 45,
  /** average seconds between anomaly rolls (rogue agent loops) */
  anomalyPeriodSec: 75,
  /** chance an anomaly fires per roll, per player */
  anomalyChance: 0.35,
  /** anomalous agents multiply their token burn by this and earn nothing */
  anomalyTokenMult: 4,
  /** anomaly self-terminates after this long if unhandled */
  anomalyDurationSec: 25,
  cacheAuraRadius: 6,
  /** token cost multiplier inside a cache layer aura */
  cacheAuraMult: 0.65,
  /** market saturation: each serving agent beyond the first cuts the
   * per-serve payout for EVERYONE at that market (supply and demand) */
  saturationPerServer: 0.12,
  /** payout never drops below this fraction of base */
  saturationFloor: 0.4,
  /** base data storage without warehouses */
  dataCapBase: 80,
  /** data consumed per serve (context freshness); doctrine multiplies this */
  serveDataCost: 0.8,
  /** revenue multiplier when serving with an empty data pool ("stale model") */
  staleRevenueMult: 0.45,
  /** RAG doctrine: data cost x, revenue x */
  ragDataCostMult: 2.2,
  ragRevenueMult: 1.25,
  /** fine-tune doctrine: data cost x */
  fineTuneDataCostMult: 0.6,
  /** fine-tuning job: one-off investment for a permanent revenue bonus */
  fineTuneCostData: 120,
  fineTuneCostTokens: 300,
  fineTuneTimeSec: 45,
  fineTuneRevenueBonus: 0.08,
  fineTuneMaxJobs: 3,
  /** brownout: token generation & training scale by cap/draw when draw > cap */
  brownoutFloor: 0.25,
  /** markets relocate on average this often (staggered/randomized per market) */
  marketMoveMinSec: 100,
  marketMoveMaxSec: 150,
  /** when total map data drops below this, fresh nodes start appearing */
  dataRespawnThreshold: 1200,
  dataRespawnCheckSec: 45,
  dataRespawnAmount: 700,
  /** Synthetic Data Plant: tokens in -> data out, per second */
  syntheticTokensPerSec: 3,
  syntheticDataPerSec: 1.1,
  /** serverless faction: $ paid per token-equivalent per serve (the
   * per-invocation fee); flows through the same caching/routing discounts.
   * This is the faction's production cost — it skips the entire GPU
   * cluster + substation capex layer, so the meter has to be expensive
   * enough that pay-as-you-go isn't strictly better than provisioning. */
  serverlessDollarPerToken: 1.35,
} as const;

// ---------------------------------------------------------------------------
// Economy 3.0: the frontier race
// ---------------------------------------------------------------------------
//
// A global FRONTIER GENERATION tracks the best model anyone has shipped.
// Serve-agents are stamped with the generation they were trained at; the
// market prices them RELATIVE to the frontier — when a rival ships, your
// fleet doesn't get worse, it gets repriced (the "GPT-4 the day Opus landed"
// effect). Old agents aren't useless, they're downmarket: premium segments
// only pay their multiplier to near-frontier models, bulk segments take
// anything. Advancing costs escalate per generation (scaling laws), but
// catching up to a generation someone else already shipped is discounted
// (fast-follow / distillation). Shipping a new generation resets your
// fine-tuning jobs — fine-tunes don't transfer to a new base model; RAG does.

export const FRONTIER = {
  /** base cost of the gen-1 training run (before growth/fast-follow).
   *  Data is deliberately the lightest ingredient: late-game serving fleets
   *  drain the data pool continuously, so a high data price locks the race
   *  behind a resource that never accumulates. */
  baseCostBudget: 500,
  baseCostData: 60,
  baseCostTokens: 200,
  /** scaling laws: each successive generation costs this much more */
  costGrowth: 1.7,
  /** wall-clock length of a training run (brownout-throttled) */
  runTimeSec: 60,
  /** cost multiplier when the target generation already exists somewhere
   *  (second place is cheaper — distillation and published techniques) */
  fastFollowMult: 0.55,
  /** revenue multiplier lost per generation behind the global frontier */
  gapRevenuePenalty: 0.25,
  /** relegated agents never earn less than this fraction */
  gapRevenueFloor: 0.4,
  /** premium market segments pay this multiplier... */
  premiumRevenueMult: 1.6,
  /** ...but only to agents within this many generations of the frontier */
  premiumGenSlack: 1,
  /** launch buzz: pushing the frontier FIRST earns a temporary premium */
  launchBuzzMult: 1.3,
  launchBuzzSec: 40,
} as const;

// ---------------------------------------------------------------------------
// Cyber operations — offense/defense tuning
// ---------------------------------------------------------------------------
//
// Modeled on the anomaly system: an attack inflicts a timed debuff on a rival,
// and defensive buildings/techs shrink or absorb it. Launched from an
// operational Adversarial Lab at an enemy building; each has a budget/data
// cost and its own cooldown. Defenses: an operational Security Operations
// Center (socCenter) halves every incoming effect, and one dedicated tech per
// attack halves it again (or, for injection, halves the number of agents hit).
//
// Phase gating mirrors the FinOps maturity model: the AI-native attack
// (Prompt Injection) and its counter (Input Sanitization) open at Walk; the
// heavier infrastructure attacks (DDoS, Data Poisoning) and their counters
// (Rate Limiting, Data Provenance) open at Run. A defence is never gated
// later than the attack it stops.

export const CYBER = {
  promptInjection: {
    /** minimum maturity phase to launch (Walk) */
    phase: 1,
    costBudget: 120,
    costData: 15,
    cooldownSec: 30,
    /** enemy serve-agents within this radius of the target become anomalous */
    radius: 7,
    /** base number of nearby agents forced into a runaway loop */
    maxTargets: 4,
  },
  ddos: {
    /** minimum maturity phase to launch (Run) */
    phase: 2,
    costBudget: 180,
    costData: 10,
    cooldownSec: 40,
    /** seconds the target building is knocked offline (before defenses) */
    durationSec: 10,
  },
  dataPoison: {
    /** minimum maturity phase to launch (Run) */
    phase: 2,
    costBudget: 150,
    costData: 0,
    cooldownSec: 35,
    /** data scrubbed from the victim's pool (before defenses) */
    drainData: 40,
    /** seconds of the revenue penalty below (before defenses) */
    durationSec: 18,
    /** revenue multiplier while poisoned */
    revenueMult: 0.6,
  },
  /** operational Security Operations Center: multiplies every incoming effect
   * (injection agent count, DDoS duration, poison drain + duration) */
  socResistMult: 0.5,
  /** Input Sanitization tech: multiplies injection agent count */
  sanitizeResistMult: 0.5,
  /** Rate Limiting (WAF) tech: multiplies DDoS duration */
  wafResistMult: 0.5,
  /** Data Provenance tech: multiplies poison duration AND drain */
  provenanceResistMult: 0.5,
} as const;

/** Revenue multiplier at a market with `servers` active serving agents. */
export function marketSaturationMult(servers: number): number {
  return Math.max(
    ECONOMY.saturationFloor,
    1 - ECONOMY.saturationPerServer * Math.max(0, servers - 1),
  );
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

export interface BuildingStats {
  name: string;
  desc: string;
  costBudget: number;
  costData: number;
  buildTimeSec: number;
  hp: number;
  /** $/sec upkeep — the cloud bill */
  burnPerSec: number;
  /** tokens/sec generated (gpuCluster) */
  tokensPerSec: number;
  /** added to the player's token pool cap */
  tokenCapAdd: number;
  /** power drawn while operational; total draw > cap = brownout */
  powerDraw: number;
  /** power supplied while operational (HQ, substations) */
  powerCapAdd: number;
  /** added to the player's data storage cap */
  dataCapAdd: number;
  /** must have an operational building of this type first */
  requires?: BuildingType;
  phase: Phase; // minimum maturity phase required
  size: number; // square footprint in tiles
  trains: UnitType[];
  sight: number;
  /** attack stats for defensive structures */
  attack?: { damage: number; range: number; periodSec: number };
}

export const BUILDINGS: Record<BuildingType, BuildingStats> = {
  hq: {
    name: 'Cloud Console',
    desc: 'Your command center. Trains engineers, accepts data drop-off, advances maturity phases. Lose it and stay insolvent — game over.',
    costBudget: 1200,
    costData: 0,
    buildTimeSec: 45,
    hp: 1400,
    burnPerSec: 1.0,
    tokensPerSec: 1,
    tokenCapAdd: 100,
    powerDraw: 0,
    powerCapAdd: 10,
    dataCapAdd: 0,
    phase: 0,
    size: 3,
    trains: ['engineer', 'scoutCrawler'],
    sight: 9,
  },
  gpuCluster: {
    name: 'GPU Cluster',
    desc: 'Generates token capacity. Expensive to run — the biggest line item on your bill.',
    costBudget: 350,
    costData: 0,
    buildTimeSec: 18,
    hp: 450,
    burnPerSec: 3.2,
    tokensPerSec: 9,
    tokenCapAdd: 150,
    powerDraw: 5,
    powerCapAdd: 0,
    dataCapAdd: 0,
    phase: 0,
    size: 2,
    trains: [],
    sight: 6,
  },
  inferenceEndpoint: {
    name: 'Inference Endpoint',
    desc: 'Deploys inference agents that serve market demand for revenue.',
    costBudget: 300,
    costData: 0,
    buildTimeSec: 16,
    hp: 500,
    burnPerSec: 1.2,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 1,
    powerCapAdd: 0,
    dataCapAdd: 0,
    requires: 'gpuCluster',
    phase: 0,
    size: 2,
    trains: ['inferenceAgent', 'burstWorker', 'distilledAgent', 'guardrailSentinel', 'reservedInstance', 'redTeamAgent', 'loadTester', 'spotInstance', 'forkNode'],
    sight: 6,
  },
  dataWarehouse: {
    name: 'Data Warehouse',
    desc: 'Data drop-off point. Build near data nodes to shorten harvest loops.',
    costBudget: 180,
    costData: 0,
    buildTimeSec: 12,
    hp: 400,
    burnPerSec: 0.6,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 1,
    powerCapAdd: 0,
    dataCapAdd: 150,
    phase: 0,
    size: 2,
    trains: [],
    sight: 5,
  },
  finopsCenter: {
    name: 'FinOps Command Center',
    desc: 'Unlocks cost-optimization research and auto-remediates anomalies. Knowledge is margin.',
    costBudget: 400,
    costData: 20,
    buildTimeSec: 20,
    hp: 550,
    burnPerSec: 0.8,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 1,
    powerCapAdd: 0,
    dataCapAdd: 0,
    phase: 0,
    size: 2,
    trains: [],
    sight: 7,
  },
  cacheLayer: {
    name: 'Cache Layer',
    desc: 'Prompt/context cache. Nearby agents spend fewer tokens per action (aura).',
    costBudget: 250,
    costData: 15,
    buildTimeSec: 14,
    hp: 350,
    burnPerSec: 0.5,
    tokensPerSec: 0,
    tokenCapAdd: 50,
    powerDraw: 2,
    powerCapAdd: 0,
    dataCapAdd: 0,
    requires: 'inferenceEndpoint',
    phase: 1,
    size: 2,
    trains: [],
    sight: 6,
  },
  firewall: {
    name: 'Guardrail Tower',
    desc: 'Static defense. Filters hostile traffic the hard way. Watch the burn — camping is a line item.',
    costBudget: 220,
    costData: 0,
    buildTimeSec: 15,
    hp: 600,
    burnPerSec: 1.3,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 1,
    powerCapAdd: 0,
    dataCapAdd: 0,
    phase: 1,
    size: 1,
    trains: [],
    sight: 8,
    attack: { damage: 14, range: 7, periodSec: 1.0 },
  },
  trainingCluster: {
    name: 'Training Cluster',
    desc: 'Fine-tunes frontier-class agents. Enormous burn; enormous capability.',
    costBudget: 700,
    costData: 60,
    buildTimeSec: 30,
    hp: 700,
    burnPerSec: 4.5,
    tokensPerSec: 0,
    tokenCapAdd: 100,
    powerDraw: 8,
    powerCapAdd: 0,
    dataCapAdd: 0,
    requires: 'dataWarehouse',
    phase: 2,
    size: 3,
    trains: ['redTeamAgent', 'guardrailSentinel', 'loadTester', 'frontierAgent', 'superCluster'],
    sight: 7,
  },
  syntheticData: {
    name: 'Synthetic Data Plant',
    desc: 'Distills tokens into synthetic training data (3 tokens/s -> 1.1 data/s). The answer to a mined-out map.',
    costBudget: 450,
    costData: 30,
    buildTimeSec: 22,
    hp: 480,
    burnPerSec: 1.8,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 4,
    powerCapAdd: 0,
    dataCapAdd: 50,
    requires: 'dataWarehouse',
    phase: 2,
    size: 2,
    trains: [],
    sight: 5,
  },
  substation: {
    name: 'Substation',
    desc: 'Adds power capacity. Produces nothing, costs money, and you cannot scale without it — the real AI datacenter bottleneck.',
    costBudget: 250,
    costData: 0,
    buildTimeSec: 14,
    hp: 420,
    burnPerSec: 0.8,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 0,
    powerCapAdd: 12,
    dataCapAdd: 0,
    phase: 0,
    size: 2,
    trains: [],
    sight: 5,
  },
  adversarialLab: {
    name: 'Adversarial Lab',
    desc: 'Offensive cyber ops: prompt injection, DDoS, and data poisoning against rivals. One lab is enough; abilities share no queue, only cooldowns.',
    costBudget: 380,
    costData: 20,
    buildTimeSec: 22,
    hp: 460,
    burnPerSec: 1.6,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 3,
    powerCapAdd: 0,
    dataCapAdd: 0,
    requires: 'inferenceEndpoint',
    phase: 1,
    size: 2,
    trains: [],
    sight: 6,
  },
  socCenter: {
    name: 'Security Operations Center',
    desc: 'Blue team HQ. Halves every incoming cyber attack and unlocks security research (input sanitization, rate limiting, data provenance).',
    costBudget: 300,
    costData: 15,
    buildTimeSec: 18,
    hp: 560,
    burnPerSec: 1.0,
    tokensPerSec: 0,
    tokenCapAdd: 0,
    powerDraw: 2,
    powerCapAdd: 0,
    dataCapAdd: 0,
    phase: 1,
    size: 2,
    trains: [],
    sight: 7,
  },
};

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

export interface UnitStats {
  name: string;
  desc: string;
  costBudget: number;
  costData: number;
  trainTimeSec: number;
  hp: number;
  /** $/sec upkeep */
  burnPerSec: number;
  /** tokens/sec consumed just existing (idle inference is not free) */
  idleTokensPerSec: number;
  speed: number; // tiles/sec
  sight: number;
  phase: Phase;
  /** siege units can only attack buildings and never auto-engage units */
  siege?: boolean;
  /** only this faction can train it */
  faction?: FactionId;
  /** can serve market demand for revenue */
  serves?: boolean;
  /** multiplier on serve period (0.5 = serves twice as fast) */
  servePeriodMult?: number;
  /** multiplier on revenue per serve for this unit */
  serveRevenueMult?: number;
  /** multiplier on token/data cost per serve for this unit */
  serveCostMult?: number;
  /** unit despawns this many seconds after spawning */
  ttlSec?: number;
  attack?: { damage: number; range: number; periodSec: number };
}

export const UNITS: Record<UnitType, UnitStats> = {
  engineer: {
    name: 'Platform Engineer',
    desc: 'Builds infrastructure and harvests data nodes.',
    costBudget: 60,
    costData: 0,
    trainTimeSec: 8,
    hp: 60,
    burnPerSec: 0.25,
    idleTokensPerSec: 0,
    speed: 2.4,
    sight: 6,
    phase: 0,
    attack: { damage: 3, range: 1, periodSec: 1.2 },
  },
  inferenceAgent: {
    name: 'Inference Agent',
    desc: 'Serves market demand: consumes tokens, produces revenue. Your margin lives and dies here.',
    serves: true,
    costBudget: 90,
    costData: 0,
    trainTimeSec: 10,
    hp: 70,
    burnPerSec: 0.35,
    idleTokensPerSec: 0.4,
    speed: 2.2,
    sight: 6,
    phase: 0,
  },
  scoutCrawler: {
    name: 'Scout Crawler',
    desc: 'Fast, fragile map vision. Its probe sting is more insult than injury — perfect for pestering gatherers.',
    costBudget: 40,
    costData: 0,
    trainTimeSec: 6,
    hp: 35,
    burnPerSec: 0.15,
    idleTokensPerSec: 0.1,
    speed: 4.2,
    sight: 9,
    phase: 0,
    attack: { damage: 2, range: 3, periodSec: 1.2 },
  },
  redTeamAgent: {
    name: 'Red Team Agent',
    desc: 'Offensive unit. Probes rival infrastructure until it stops responding.',
    costBudget: 140,
    costData: 10,
    trainTimeSec: 12,
    hp: 110,
    burnPerSec: 0.6,
    idleTokensPerSec: 0.5,
    speed: 2.8,
    sight: 7,
    phase: 1,
    attack: { damage: 12, range: 4, periodSec: 1.0 },
  },
  guardrailSentinel: {
    name: 'Guardrail Sentinel',
    desc: 'Defensive escort. Tanky, protective, dull at parties.',
    costBudget: 120,
    costData: 5,
    trainTimeSec: 11,
    hp: 180,
    burnPerSec: 0.5,
    idleTokensPerSec: 0.3,
    speed: 2.0,
    sight: 6,
    phase: 1,
    attack: { damage: 8, range: 2, periodSec: 1.0 },
  },
  loadTester: {
    name: 'Load Tester',
    desc: 'Siege engine: hammers infrastructure from beyond tower range (9 vs 7). Cannot target units at all — escort it or lose it. A Run-phase base-cracker.',
    costBudget: 220,
    costData: 15,
    trainTimeSec: 16,
    hp: 90,
    burnPerSec: 0.7,
    idleTokensPerSec: 0.6,
    speed: 1.5,
    sight: 9,
    phase: 2,
    siege: true,
    attack: { damage: 30, range: 9, periodSec: 2.5 },
  },
  burstWorker: {
    name: 'Burst Worker',
    desc: 'Ephemeral function-as-a-unit: serves ~1.6x rate and despawns after 75 seconds. Spin up for demand spikes.',
    costBudget: 95,
    costData: 0,
    trainTimeSec: 4,
    hp: 45,
    burnPerSec: 0.3,
    idleTokensPerSec: 0,
    speed: 3.0,
    sight: 5,
    phase: 0,
    faction: 'serverless',
    serves: true,
    servePeriodMult: 0.62,
    ttlSec: 75,
  },
  reservedInstance: {
    name: 'Reserved Instance',
    desc: 'Prepaid muscle: expensive up front, nearly free to keep (\$0.05/s), tanky. The commitment-discount philosophy with a shield.',
    costBudget: 260,
    costData: 0,
    trainTimeSec: 14,
    hp: 260,
    burnPerSec: 0.05,
    idleTokensPerSec: 0.2,
    speed: 1.6,
    sight: 6,
    phase: 1,
    faction: 'hyperscale',
    attack: { damage: 10, range: 3, periodSec: 1.0 },
  },
  distilledAgent: {
    name: 'Distilled Agent',
    desc: 'Small community model: half the tokens and data per serve for 75% of the revenue. Efficiency as ideology.',
    costBudget: 60,
    costData: 0,
    trainTimeSec: 7,
    hp: 55,
    burnPerSec: 0.25,
    idleTokensPerSec: 0.2,
    speed: 2.2,
    sight: 6,
    phase: 0,
    faction: 'openweights',
    serves: true,
    serveRevenueMult: 0.75,
    serveCostMult: 0.5,
  },
  frontierAgent: {
    name: 'Frontier Agent',
    desc: 'Run-phase apex unit. Devastating, and it burns tokens like a bonfire.',
    costBudget: 320,
    costData: 40,
    trainTimeSec: 22,
    hp: 320,
    burnPerSec: 1.6,
    idleTokensPerSec: 2.0,
    speed: 2.4,
    sight: 8,
    phase: 2,
    attack: { damage: 26, range: 5, periodSec: 1.1 },
  },
  superCluster: {
    name: 'Supercluster',
    desc: 'HYPERSCALE Run apex: a walking datacenter. Enormous HP and damage, enormous bill. Capital as a weapon.',
    costBudget: 520,
    costData: 60,
    trainTimeSec: 26,
    hp: 620,
    burnPerSec: 2.6,
    idleTokensPerSec: 3.0,
    speed: 1.7,
    sight: 8,
    phase: 2,
    faction: 'hyperscale',
    attack: { damage: 40, range: 5, periodSec: 1.2 },
  },
  spotInstance: {
    name: 'Spot Instance',
    desc: 'SERVERLESS Run raider: cheap, fast, hits hard — but reclaimed after 45s. Spin up a swarm for a timed all-in. The Spot to Hyperscale\'s Reserved.',
    costBudget: 75,
    costData: 0,
    trainTimeSec: 5,
    hp: 55,
    burnPerSec: 0.2,
    idleTokensPerSec: 0,
    speed: 3.4,
    sight: 6,
    phase: 2,
    faction: 'serverless',
    ttlSec: 45,
    attack: { damage: 16, range: 3, periodSec: 1.0 },
  },
  forkNode: {
    name: 'Fork Node',
    desc: 'OPENWEIGHTS Run swarm: dirt-cheap and weak alone — fork enough of them and the community overwhelms. Win by numbers.',
    costBudget: 45,
    costData: 0,
    trainTimeSec: 5,
    hp: 55,
    burnPerSec: 0.2,
    idleTokensPerSec: 0.15,
    speed: 2.6,
    sight: 6,
    phase: 2,
    faction: 'openweights',
    attack: { damage: 9, range: 2, periodSec: 1.0 },
  },
};

// ---------------------------------------------------------------------------
// Techs (FinOps capabilities)
// ---------------------------------------------------------------------------

export interface TechStats {
  name: string;
  desc: string;
  costBudget: number;
  costData: number;
  researchTimeSec: number;
  phase: Phase;
  requires?: TechId;
  /** which building can research it (one research at a time PER LAB TYPE) */
  lab: BuildingType;
  /** factions that cannot research this (usually: they have it innately) */
  notFaction?: FactionId;
}

export const TECHS: Record<TechId, TechStats> = {
  taggingPolicy: {
    name: 'Tagging Policy',
    desc: 'Allocation metadata on everything. +10% revenue per serve (you finally know what makes money).',
    costBudget: 120,
    costData: 10,
    researchTimeSec: 15,
    phase: 0,
    lab: 'finopsCenter',
  },
  focusAdoption: {
    name: 'FOCUS Adoption',
    desc: 'Normalized billing data (FinOps Open Cost & Usage Specification). Reveals rivals\' burn and revenue in the scoreboard.',
    costBudget: 150,
    costData: 15,
    researchTimeSec: 18,
    phase: 0,
    requires: 'taggingPolicy',
    lab: 'finopsCenter',
  },
  promptCaching: {
    name: 'Prompt Caching',
    desc: 'Reuse shared context. -30% token cost per serve.',
    costBudget: 200,
    costData: 20,
    researchTimeSec: 20,
    phase: 1,
    lab: 'inferenceEndpoint',
  },
  modelRouting: {
    name: 'Model Routing',
    desc: 'Send easy work to smaller models. -20% token cost per serve and -15% agent training cost.',
    costBudget: 220,
    costData: 25,
    researchTimeSec: 22,
    phase: 1,
    requires: 'promptCaching',
    lab: 'inferenceEndpoint',
  },
  batchInference: {
    name: 'Batch Inference',
    desc: 'Trade latency for throughput: serves are 40% slower but earn 80% more.',
    costBudget: 180,
    costData: 20,
    researchTimeSec: 18,
    phase: 1,
    requires: 'promptCaching',
    lab: 'inferenceEndpoint',
  },
  commitmentDiscount1: {
    name: 'Commitment Tier I',
    desc: 'Committed-use discounts: pay $400 up front, -20% building burn forever.',
    costBudget: 400,
    costData: 0,
    researchTimeSec: 10,
    phase: 1,
    lab: 'finopsCenter',
  },
  commitmentDiscount2: {
    name: 'Commitment Tier II',
    desc: 'Deeper commitment: pay $800 up front, a further -20% building burn.',
    costBudget: 800,
    costData: 0,
    researchTimeSec: 10,
    phase: 2,
    requires: 'commitmentDiscount1',
    lab: 'finopsCenter',
  },
  anomalyDetection: {
    name: 'Anomaly Detection',
    desc: 'Runaway agent loops are auto-remediated in 3 seconds instead of raging on.',
    costBudget: 160,
    costData: 15,
    researchTimeSec: 15,
    phase: 1,
    lab: 'finopsCenter',
  },
  autoscaling: {
    name: 'Autoscaling',
    desc: 'Scale to zero-ish: idle units consume 60% fewer idle tokens. (Serverless has this innately and cannot research it.)',
    notFaction: 'serverless',
    costBudget: 140,
    costData: 10,
    researchTimeSec: 14,
    phase: 1,
    requires: 'modelRouting',
    lab: 'inferenceEndpoint',
  },
  unitEconomics: {
    name: 'Unit Economics Program',
    desc: 'Cost-per-outcome discipline across the org. +20% revenue per serve.',
    costBudget: 300,
    costData: 30,
    researchTimeSec: 25,
    phase: 2,
    requires: 'taggingPolicy',
    lab: 'finopsCenter',
  },
  inputSanitization: {
    name: 'Input Sanitization',
    desc: 'Untrusted input is scrubbed before it reaches your models. Prompt injection compromises half as many of your agents.',
    costBudget: 180,
    costData: 15,
    researchTimeSec: 16,
    phase: 1,
    lab: 'socCenter',
  },
  rateLimiting: {
    name: 'Rate Limiting (WAF)',
    desc: 'A web application firewall sheds hostile floods. DDoS knocks your buildings offline for half as long.',
    costBudget: 180,
    costData: 15,
    researchTimeSec: 16,
    phase: 2,
    lab: 'socCenter',
  },
  dataProvenance: {
    name: 'Data Provenance',
    desc: 'Signed, verifiable data lineage. Poisoning scrubs half the data and its stale-context penalty fades twice as fast.',
    costBudget: 200,
    costData: 20,
    researchTimeSec: 18,
    phase: 2,
    lab: 'socCenter',
  },
  speculativeDecoding: {
    name: 'Speculative Decoding',
    desc: 'A draft model guesses ahead and the big model just checks its work. -25% token cost per serve on top of everything else.',
    costBudget: 320,
    costData: 30,
    researchTimeSec: 24,
    phase: 2,
    requires: 'modelRouting',
    lab: 'inferenceEndpoint',
  },
  testTimeCompute: {
    name: 'Test-Time Compute',
    desc: 'Let agents think longer before acting. +25% damage from all your combat units.',
    costBudget: 360,
    costData: 40,
    researchTimeSec: 26,
    phase: 2,
    lab: 'finopsCenter',
  },
};

// ---------------------------------------------------------------------------
// Phases (maturity model)
// ---------------------------------------------------------------------------

export interface PhaseStats {
  name: string;
  desc: string;
  costBudget: number;
  costData: number;
  timeSec: number;
}

/** Cost to advance FROM phase i to i+1 */
export const PHASE_ADVANCE: PhaseStats[] = [
  {
    name: 'Walk',
    desc: 'From ad-hoc cost awareness to real practice. Unlocks optimization techs, caches, defense.',
    costBudget: 500,
    costData: 40,
    timeSec: 30,
  },
  {
    name: 'Run',
    desc: 'FinOps at scale. Unlocks training clusters, frontier agents, deep commitments.',
    costBudget: 1100,
    costData: 100,
    timeSec: 45,
  },
];

// ---------------------------------------------------------------------------
// Factions — trade-off identities
// ---------------------------------------------------------------------------

export interface FactionStats {
  name: string;
  tagline: string;
  desc: string;
  /** multiplier on all building burn */
  buildingBurnMult: number;
  /** multiplier on all unit burn */
  unitBurnMult: number;
  /** multiplier on token generation */
  tokenGenMult: number;
  /** multiplier on token cost of serves */
  tokenCostMult: number;
  /** multiplier on revenue per serve */
  revenueMult: number;
  /** multiplier on build/train times (lower = faster) */
  buildSpeedMult: number;
  /** multiplier on unit/building $ costs */
  costMult: number;
  /** extra starting budget */
  budgetBonus: number;
  /** serverless: no token pool for serves — each serve pays a $ fee instead
   * (pure OpEx); also exempt from GPU Cluster requirements/builds */
  tokenless?: boolean;
  /** hyperscale: substations supply this much power instead of the default */
  substationPowerOverride?: number;
  /** openweights: engineer gather-rate multiplier (community datasets) */
  gatherRateMult?: number;
  /** multiplier on frontier training-run costs. Provisioned capacity is what
   *  actually trains frontier models — hyperscale runs cheap; renting
   *  frontier-scale compute on demand (serverless) costs a premium. */
  frontierCostMult?: number;
  /** faction override of FRONTIER.fastFollowMult (openweights: distillation
   *  is the community's home turf — catching up is even cheaper) */
  fastFollowMult?: number;
}

export const FACTIONS: Record<FactionId, FactionStats> = {
  hyperscale: {
    name: 'Hyperscale Titans',
    tagline: 'Enterprise scale, enterprise bills.',
    desc: 'Deep pockets and raw capacity: +25% token generation, +$500 start, and bulk-provisioned Substations (+18 power each), but everything burns hotter (+15% building burn) and ships slower (+10% build times).',
    buildingBurnMult: 1.15,
    unitBurnMult: 1.0,
    tokenGenMult: 1.25,
    tokenCostMult: 1.0,
    revenueMult: 1.0,
    buildSpeedMult: 1.1,
    costMult: 1.0,
    budgetBonus: 500,
    substationPowerOverride: 18,
    frontierCostMult: 0.7, // provisioned training capacity: big runs are their game
  },
  serverless: {
    name: 'Serverless Guild',
    tagline: 'Scale to zero, sprint to market.',
    desc: 'Pay-as-you-go DNA: NO GPU clusters — serves pay a per-invocation fee instead of drawing tokens (pure OpEx), units scale to zero when idle, -20% building burn, 15% faster builds. Win before the meter catches up.',
    buildingBurnMult: 0.8,
    unitBurnMult: 0.9,
    tokenGenMult: 0.9,
    tokenCostMult: 1.0,
    revenueMult: 1.0,
    buildSpeedMult: 0.85,
    costMult: 0.95,
    budgetBonus: 0,
    tokenless: true,
    frontierCostMult: 1.35, // nobody pretrains on demand: rented frontier compute costs a premium
  },
  openweights: {
    name: 'Open Weights Collective',
    tagline: 'Self-hosted. Community-tuned. Data-hungry.',
    desc: 'Self-hosted models make serving cheap (-25% token cost) and the community gathers data 40% faster, but revenue per serve is thinner (-10%) and premium units cost extra data.',
    buildingBurnMult: 0.9,
    unitBurnMult: 1.0,
    tokenGenMult: 1.0,
    tokenCostMult: 0.75,
    revenueMult: 0.9,
    buildSpeedMult: 1.0,
    costMult: 1.0,
    budgetBonus: 0,
    gatherRateMult: 1.4,
    fastFollowMult: 0.4, // distillation DNA: the community catches up cheapest
  },
};
